import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

function LandingPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [statsLoaded, setStatsLoaded] = useState(false);

  useEffect(() => {
    fetch('/api/public/stats')
      .then(r => r.json())
      .then(d => { if (d.success) { setStats(d.stats); setStatsLoaded(true); } })
      .catch(() => setStatsLoaded(true));
  }, []);

  const handleNavigate = (path) => navigate(path);

  const features = [
    {
      icon: '📱',
      title: 'Mobile-first werkorders',
      tekst: 'Monteurs werken op telefoon. Foto\'s, GPS-navigatie, handtekening — alles op de werkplek.'
    },
    {
      icon: '✍️',
      title: 'Forms + QuickSign',
      tekst: 'Vervang papieren werkbonnen. Meerdere ondertekenaars, automatisch PDF terug, eigen huisstijl.'
    },
    {
      icon: '🌐',
      title: 'Klantenportaal',
      tekst: 'Klanten melden storingen zelf, volgen status, ontvangen werkbonnen automatisch.'
    },
    {
      icon: '🔁',
      title: 'Onderhoud forecast',
      tekst: 'Cyclisch onderhoud automatisch ingepland. Niks vergeten, klanten op tijd geholpen.'
    },
    {
      icon: '🔌',
      title: 'Echte integraties',
      tekst: 'TopDesk, SharePoint, SAP, WebAuthn, Magic Link — sluit aan op wat je al hebt.'
    }
  ];

  const modules = [
    'Werkorders', 'Planning', 'Klantenboek', 'Projecten', 'Materialen', 'Toolboxen',
    'Cursussen', 'Onderhoud', 'Storingen', 'Forms', 'QuickSign', 'LMRA',
    'Kennisbank', 'Documenten', 'Audit log', 'Klantportaal', 'Magic Link', 'Pasjes',
    'Forecast', 'Restwerk', 'Templates', 'Uren-export', 'TopDesk', 'SharePoint',
    'SAP', 'WebAuthn', 'Rollen/Rechten', 'Multi-tenant', 'Branding', 'Dashboard'
  ];

  const prijzen = [
    {
      naam: 'Forms Only', prijs: 29, ondertitel: 'Alleen Forms-product',
      features: ['Onbeperkt formulieren', 'Quick-Sign handtekening', 'PDF + branding', 'Tot 5 gebruikers', 'Email support']
    },
    {
      naam: 'Starter', prijs: 199, ondertitel: 'WOMsys werkorders',
      features: ['Werkorder beheer', 'Klantenboek + projecten', 'Planning & monteurs', 'Tot 5 gebruikers', 'Email support']
    },
    {
      naam: 'Professional', prijs: 399, popular: true, ondertitel: 'Werkorders + Forms',
      features: ['Alles uit Starter', 'Forms-module', 'Templates & restwerk', 'Onderhoud forecast', 'Tot 20 gebruikers', 'Prioriteit support']
    },
    {
      naam: 'Business', prijs: 699, ondertitel: 'Voor grotere teams',
      features: ['Alles uit Professional', 'Onbeperkt gebruikers', 'API toegang', 'Custom integraties', 'Dedicated support', 'SLA garantie']
    },
    {
      naam: 'Enterprise', prijsLabel: 'Op aanvraag', ondertitel: 'Maatwerk',
      features: ['Alles uit Business', 'White-label', 'On-premise mogelijk', 'Custom development', 'Persoonlijke onboarding', '24/7 support']
    }
  ];

  const faqs = [
    { v: 'Hoe lang is de gratis trial?', a: '14 dagen, geen creditcard nodig. Daarna kies je een pakket of stopt het automatisch.' },
    { v: 'Per maand opzegbaar?', a: 'Ja. Geen lange contracten, opzeggen wanneer je wilt.' },
    { v: 'Hoe veilig is mijn data?', a: 'Servers in Nederland, dagelijkse backups, audit log per actie, WebAuthn passwordless login mogelijk. GDPR-compliant.' },
    { v: 'Eigen huisstijl op formulieren?', a: 'Ja. Logo, footer en QR-code op alle PDFs. Per bedrijf instelbaar.' },
    { v: 'Werkt het ook voor 1 monteur?', a: 'Ja. Forms Only voor €29/mnd voor de instap, of Starter voor werkorders.' },
    { v: 'Krijg ik mijn data eruit?', a: 'Ja. Exports beschikbaar in CSV, PDF en Excel formaat.' }
  ];

  return (
    <div className="landing-page">
      {/* HERO */}
      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-title">
            <span className="gradient-text">WOMsys</span>
            <br />
            Het complete platform voor<br />technische dienstverleners
          </h1>
          <p className="hero-subtitle">
            Van werkorder tot factuur. Van papieren werkbon tot digitale handtekening.
            <br />
            Eén platform voor installatie, onderhoud en infrastructuur.
          </p>
          <div className="hero-buttons">
            <button onClick={() => handleNavigate('/registreer')} className="btn-primary-large">
              Probeer 14 dagen gratis
            </button>
            <button onClick={() => handleNavigate('/login')} className="btn-secondary-large">
              Inloggen →
            </button>
          </div>
          <p className="hero-note">✓ Geen creditcard  ✓ Per maand opzegbaar  ✓ Direct aan de slag</p>
        </div>
      </section>

      {/* LIVE STATS BAR */}
      {statsLoaded && stats && (
        <section className="stats-bar">
          <div className="container stats-grid">
            <Stat value={stats.uptime_percentage + '%'} label="Uptime (30 dgn)" pulse />
            <Stat value={stats.forms_verstuurd} label="Forms verstuurd" />
            <Stat value={stats.werkorders_afgerond} label="Werkorders afgerond" />
            <Stat value={stats.handtekeningen} label="Handtekeningen" />
            <Stat value={stats.fotos_geupload} label="Foto's geüpload" />
            <Stat value={stats.modules + '+'} label="Modules" />
          </div>
          <div className="container stats-note">
            Live cijfers · laatst bijgewerkt {new Date(stats.last_updated).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </section>
      )}

      {/* TOP 5 FEATURES */}
      <section className="features-section">
        <div className="container">
          <h2 className="section-title">Wat WOMsys uniek maakt</h2>
          <p className="section-subtitle">Vijf functies die het verschil maken op de werkvloer</p>
          <div className="features-grid">
            {features.map((f, i) => (
              <div key={i} className="feature-card-large">
                <div className="feature-icon-large">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.tekst}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WAAROM WOMSYS */}
      <section className="waarom-section">
        <div className="container">
          <h2 className="section-title">Waarom WOMsys</h2>
          <div className="waarom-grid">
            <div className="waarom-card">
              <h3>vs Excel of papier</h3>
              <p>Geen losse sheets meer. Alles op één plek, mobiel raadpleegbaar, met audit trail.</p>
            </div>
            <div className="waarom-card">
              <h3>vs losse tools</h3>
              <p>Eén login, één factuur, één leverancier. Geen integratie-hoofdpijn meer.</p>
            </div>
            <div className="waarom-card">
              <h3>vs zelfbouw</h3>
              <p>Bespaart €100k+ aan dev-kosten. Geen onderhoud, gewoon gebruiken.</p>
            </div>
          </div>
        </div>
      </section>

      {/* MODULES GRID */}
      <section className="modules-section">
        <div className="container">
          <h2 className="section-title">30+ modules. Eén systeem.</h2>
          <p className="section-subtitle">Alles wat een technische dienstverlener nodig heeft, onder één dak</p>
          <div className="modules-grid">
            {modules.map((m, i) => (
              <div key={i} className="module-tile">{m}</div>
            ))}
          </div>
        </div>
      </section>

      {/* PRIJZEN */}
      <section className="pricing-section">
        <div className="container">
          <h2 className="section-title">Eerlijke prijzen, geen verrassingen</h2>
          <p className="section-subtitle">Per maand opzegbaar. 14 dagen gratis proberen.</p>
          <div className="pricing-grid">
            {prijzen.map((p, i) => (
              <div key={i} className={`pricing-card ${p.popular ? 'popular' : ''}`}>
                {p.popular && <div className="popular-badge">Meest gekozen</div>}
                <h3>{p.naam}</h3>
                <p className="pricing-ondertitel">{p.ondertitel}</p>
                <div className="price">
                  {p.prijsLabel ? (
                    <span className="amount-label">{p.prijsLabel}</span>
                  ) : (
                    <>
                      <span className="amount">€{p.prijs}</span>
                      <span className="period">/mnd</span>
                    </>
                  )}
                </div>
                <ul className="features-list">
                  {p.features.map((f, j) => <li key={j}>✓ {f}</li>)}
                </ul>
                <button onClick={() => handleNavigate(p.prijsLabel ? '/registreer' : '/registreer')} className={`btn-pricing ${p.popular ? 'primary' : ''}`}>
                  {p.prijsLabel ? 'Neem contact op' : 'Start gratis'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORMS-ONLY HIGHLIGHT */}
      <section className="forms-highlight">
        <div className="container forms-highlight-inner">
          <div className="forms-highlight-text">
            <span className="badge-small">FORMS ONLY</span>
            <h2>Alleen formulieren digitaliseren?</h2>
            <p>Vervang werkbonnen, FAT-rapporten en inspectielijsten door digitale formulieren met handtekening en automatische PDF. Vanaf €29/mnd.</p>
            <a href="https://forms.womsys.nl" className="btn-secondary-large">Bekijk WOMsys Forms →</a>
          </div>
          <div className="forms-highlight-visual">
            <div className="forms-mock">📋</div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="container">
          <h2 className="section-title">Veelgestelde vragen</h2>
          <div className="faq-list">
            {faqs.map((q, i) => <FaqItem key={i} vraag={q.v} antwoord={q.a} />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <h2>Klaar om slimmer te werken?</h2>
          <p>Probeer WOMsys 14 dagen gratis. Geen creditcard, geen verplichtingen.</p>
          <div className="cta-buttons">
            <button onClick={() => handleNavigate('/registreer')} className="btn-primary-large">
              Start gratis trial
            </button>
            <button onClick={() => handleNavigate('/demo-aanvragen')} className="btn-secondary-large">
              Plan een demo
            </button>
          </div>
        </div>
      </section>

      {/* OVER ONS / FOOTER */}
      <section className="about-section">
        <div className="container about-inner">
          <h2>Over WOMsys</h2>
          <p>WOMsys is een compleet werkorder management platform voor technische dienstverleners. Gebouwd voor installatiebedrijven, onderhoudsorganisaties en infrastructuurpartners die hun werkprocessen willen digitaliseren — van eerste storingsmelding tot afgetekende werkbon.</p>
          <p>Met 30+ modules, integraties met TopDesk, SharePoint en SAP, en een mobile-first aanpak voor monteurs in het veld.</p>
          <div className="founder-card">
            <div className="founder-name">Dennis Ceulemans</div>
            <div className="founder-role">Founder & Lead Developer</div>
            <p className="founder-bio">22+ jaar in technische dienstverlening. 7 jaar bij het GVB als servicetechnicus. Bouwt WOMsys vanuit de werkvloer, niet vanuit een vergaderzaal — software voor monteurs hoort gemaakt te worden door iemand die zelf het gereedschap heeft vastgehouden.</p>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="container footer-inner">
          <div>
            <strong>WOMsys</strong> · Werkorder management voor technische dienstverleners<br />
            <a href="https://forms.womsys.nl">forms.womsys.nl</a> · onderdeel van WOMsys
          </div>
          <div className="footer-meta">© 2026 · alle rechten voorbehouden</div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ value, label, pulse }) {
  return (
    <div className="stat-tile">
      <div className={`stat-value ${pulse ? 'pulse' : ''}`}>{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

function FaqItem({ vraag, antwoord }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'open' : ''}`} onClick={() => setOpen(!open)}>
      <div className="faq-vraag">
        {vraag}
        <span className="faq-icon">{open ? '−' : '+'}</span>
      </div>
      {open && <div className="faq-antwoord">{antwoord}</div>}
    </div>
  );
}

export default LandingPage;
